import * as polyline from "@mapbox/polyline"
import { format, isToday } from "date-fns"
import { TrafficIncident } from "./routeTraffic"
import i18n from "@/lib/i18n/i18n"
import { getDateFnsLocale } from "@/lib/i18n/format"

/** Menu labels for the incident types we know by name (gps maps PTV texts to
 * the same HERE types). */
export const INCIDENT_TYPE_LABELS = {
  construction: "maps.routeTraffic.types.construction",
  roadClosure: "maps.routeTraffic.types.roadClosure",
  accident: "maps.routeTraffic.types.accident",
  laneRestriction: "maps.routeTraffic.types.laneRestriction",
  congestion: "maps.routeTraffic.types.congestion",
  other: "maps.routeTraffic.types.other",
} as const

const TYPES_WITH_OWN_ICON = new Set([
  "construction",
  "roadClosure",
  "accident",
  "laneRestriction",
  "congestion",
])

/** HERE leaves the type out now and then; such incidents count as "other". */
export const incidentTypeKey = (type?: string | null): string => type || "other"

export function incidentTypeLabel(type?: string | null): string {
  const key = incidentTypeKey(type)
  if (Object.prototype.hasOwnProperty.call(INCIDENT_TYPE_LABELS, key)) {
    return i18n.t(
      INCIDENT_TYPE_LABELS[key as keyof typeof INCIDENT_TYPE_LABELS],
    )
  }
  // e.g. "disabledVehicle" -> "Disabled vehicle"
  const words = key.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export function incidentIconId(type?: string | null): string {
  return type && TYPES_WITH_OWN_ICON.has(type)
    ? `traffic-incident-${type}`
    : "traffic-incident-generic"
}

/** All incidents on the route once each, without the ones that have ended. */
export function routeIncidents(
  legs: { incidents?: TrafficIncident[] }[] | undefined,
  now: Date,
): TrafficIncident[] {
  const seen = new Set<string>()
  const result: TrafficIncident[] = []
  for (const leg of legs ?? []) {
    for (const incident of leg.incidents ?? []) {
      if (seen.has(incident.id)) {
        continue
      }
      seen.add(incident.id)
      const end = incident.valid_until ? Date.parse(incident.valid_until) : NaN
      if (!Number.isNaN(end) && end < now.getTime()) {
        continue
      }
      result.push(incident)
    }
  }
  return result
}

export type IncidentTypeCount = { type: string; label: string; count: number }

export function countIncidentTypes(
  incidents: TrafficIncident[],
): IncidentTypeCount[] {
  const counts = new Map<string, number>()
  for (const incident of incidents) {
    const key = incidentTypeKey(incident.type)
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([type, count]) => ({ type, label: incidentTypeLabel(type), count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}

export function filterIncidentsByType(
  incidents: TrafficIncident[],
  types: string[],
): TrafficIncident[] {
  const wanted = new Set(types)
  return incidents.filter((incident) =>
    wanted.has(incidentTypeKey(incident.type)),
  )
}

export type IncidentOnRoute = { incident: TrafficIncident; km: number }

const EARTH_RADIUS_M = 6371008.8

function metersBetween(a: [number, number], b: [number, number]): number {
  const toRad = Math.PI / 180
  const dLat = (b[0] - a[0]) * toRad
  const dLng = (b[1] - a[1]) * toRad
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a[0] * toRad) * Math.cos(b[0] * toRad) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h))
}

/**
 * The route's incidents with their distance along the route, nearest first.
 * The route starts where the truck was when gps fetched it, so the km are
 * counted from there. Each incident sits on a point of its own leg.
 */
export function incidentsAlongRoute(
  legs: { polyline: string; incidents?: TrafficIncident[] }[] | undefined,
  now: Date,
): IncidentOnRoute[] {
  const current = new Set(routeIncidents(legs, now).map(({ id }) => id))
  const seen = new Set<string>()
  const result: IncidentOnRoute[] = []
  let legStartM = 0

  for (const leg of legs ?? []) {
    const points = polyline.decode(leg.polyline)
    const alongM = [0]
    for (let i = 1; i < points.length; i++) {
      alongM.push(alongM[i - 1] + metersBetween(points[i - 1], points[i]))
    }

    for (const incident of leg.incidents ?? []) {
      if (!current.has(incident.id) || seen.has(incident.id)) {
        continue
      }
      seen.add(incident.id)
      let nearest = 0
      let nearestM = Infinity
      points.forEach((point, index) => {
        const distance = metersBetween(point, [incident.lat, incident.lng])
        if (distance < nearestM) {
          nearestM = distance
          nearest = index
        }
      })
      result.push({ incident, km: (legStartM + (alongM[nearest] ?? 0)) / 1000 })
    }

    legStartM += alongM[alongM.length - 1] ?? 0
  }

  return result.sort((a, b) => a.km - b.km)
}

const formatWhen = (iso?: string | null, alwaysDate = false) => {
  const time = iso ? Date.parse(iso) : NaN
  if (Number.isNaN(time)) {
    return null
  }
  return format(time, alwaysDate || !isToday(time) ? "d MMM HH:mm" : "HH:mm", {
    locale: getDateFnsLocale(),
  })
}

/**
 * The timing lines of an incident: the delay and when the truck gets there
 * (PTV, only on the way to the first stop), and until when it is valid (HERE).
 */
export function incidentTimes(
  incident: Pick<TrafficIncident, "delay_s" | "arrives_at" | "valid_until">,
): string[] {
  const times: string[] = []
  if (incident.delay_s && incident.delay_s >= 60) {
    times.push(
      i18n.t("maps.routeTraffic.incidentDelay", {
        value: Math.round(incident.delay_s / 60),
      }),
    )
  }
  // A time in the past means the truck passed it or the route is old
  const arrivesAt = incident.arrives_at ? Date.parse(incident.arrives_at) : NaN
  const arrives =
    arrivesAt > Date.now() ? formatWhen(incident.arrives_at) : null
  if (arrives) {
    times.push(i18n.t("maps.routeTraffic.truckThere", { time: arrives }))
  }
  const until = formatWhen(incident.valid_until, true)
  if (until) {
    times.push(i18n.t("maps.routeTraffic.until", { time: until }))
  }
  return times
}
